import type { InventoryItem } from '@app/types/shop-bff/types';
import {
  Box,
  constants,
  Field,
  Skeleton,
  Stack,
} from '@redbubble/design-system';
import { defineMessages, useIntl } from '@redbubble/next/intl';
import { ReactNode, useMemo } from 'react';
import { LoadingBoundary } from '../LoadingBoundary';
import { compareDisplayOrder } from './configuratorFormUtils';
import {
  ErrorType,
  InputErrorMessage,
} from './FieldConfigurators/InputErrorMessage';
import { ConfiguratorOption, getTranslatedString } from './utils';

const messages = defineMessages({
  placeholder: { defaultMessage: 'Please select an option' },
});

export type SizeFieldConfiguratorProps = {
  locale: string;
  option: ConfiguratorOption;
  currentValue: string;
  filterValues: Array<{
    value: ConfiguratorOption['values'][number];
    inventoryItem: InventoryItem;
  }>;
  errors: Record<string, string>;
  currentProductTypeId?: string;
  sizeGuideButton?: ReactNode;
};

type SizeFieldMessageProps = {
  errors: Record<string, string>;
  attributeName: string;
  currentProductTypeId?: string;
};

const SizeFieldMessage: React.FC<SizeFieldMessageProps> = ({
  errors,
  attributeName,
}) => {
  if (errors[attributeName]) {
    return <InputErrorMessage errorType={ErrorType.Required} />;
  }
  return null;
};

type SizeFieldSkeletonProps = {
  hasSizeGuideButton: boolean;
  attributeName: string;
  currentProductTypeId?: string;
};

const SizeFieldSkeleton: React.FC<SizeFieldSkeletonProps> = ({
  hasSizeGuideButton,
}) => {
  return (
    <Box marginLeft="xs" marginRight="xs" marginBottom="l">
      <Stack spacing="xs">
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          marginBottom="xs"
        >
          <Skeleton height={24} width={130} animated roundedCorners />

          {hasSizeGuideButton && (
            <Skeleton height={24} width={150} animated roundedCorners />
          )}
        </Box>
        <Box>
          <Box marginBottom="xs">
            <Skeleton height={40} animated roundedCorners />
          </Box>
        </Box>
      </Stack>
    </Box>
  );
};

export const SizeFieldConfigurator: React.FC<SizeFieldConfiguratorProps> = ({
  locale,
  option,
  currentValue,
  filterValues,
  errors,
  sizeGuideButton,
  currentProductTypeId,
}) => {
  const label = getTranslatedString(option.publicName, option.key, locale);
  const sortedOptions = useMemo(
    () =>
      filterValues
        .map(({ value, inventoryItem }) => {
          const labelText = getTranslatedString(
            value.publicName,
            value.equivalentValue,
            locale
          );
          return {
            value: value.equivalentValue,
            label: inventoryItem.measurementSummary
              ? `${labelText} (${inventoryItem.measurementSummary})`
              : labelText,
            displayOrder: value.publicDisplayOrder,
          };
        })
        .sort(compareDisplayOrder),
    [filterValues, locale]
  );

  return (
    <LoadingBoundary
      loadingComponent={
        <SizeFieldSkeleton
          hasSizeGuideButton={!!sizeGuideButton}
          attributeName={option.key}
          currentProductTypeId={currentProductTypeId}
        />
      }
    >
      <Field
        label={label}
        fluid
        type="radio-selector"
        multiple={false}
        intent={errors[option.key] && constants.ERROR}
        name={option.key}
        value={currentValue}
        options={sortedOptions}
        placeholder={useIntl().formatMessage(messages.placeholder)}
        actionSlot={sizeGuideButton}
      >
        <SizeFieldMessage
          errors={errors}
          attributeName={option.key}
          currentProductTypeId={currentProductTypeId}
        />
      </Field>
    </LoadingBoundary>
  );
};
